import { useEffect, useState } from "react";
import { Antenna, BadgeCheck, Building2, Cable, ChevronDown, ChevronRight, Copy, Pencil, PhoneCall, Plus, Satellite, Search, ShieldCheck, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, fmtDate } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { DatePicker } from "../components/DatePicker";
import { Button } from "../components/ui/button";
import { Card, CardHeader, CardTitle } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Label } from "../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import PinConfirm from "../components/PinConfirm";

const KIND_META = {
  starlink: { title: "Data Starlink", icon: Satellite, mode: "creds", unit: "unit", subtitle: "Database unit Starlink semua cabang — kredensial login, SN KIT, paket, dan jatuh tempo." },
  telkom: { title: "Data Telkom", icon: PhoneCall, mode: "creds", unit: "unit", subtitle: "Database akun/perangkat Telkom semua cabang — kredensial login, SN, paket, dan jatuh tempo." },
  indosat: { title: "Data Indosat", icon: Antenna, mode: "creds", unit: "unit", subtitle: "Database akun/perangkat Indosat semua cabang — kredensial login, SN, paket, dan jatuh tempo." },
  moratel: { title: "Data Moratel", icon: Cable, mode: "creds", unit: "unit", subtitle: "Database akun/perangkat Moratel semua cabang — kredensial login, SN, paket, dan jatuh tempo." },
  indibiz: { title: "Data Indibiz", icon: Building2, mode: "indibiz", unit: "akun", subtitle: "Rekapitulasi data langganan Indibiz Telkom — nomor layanan, pelanggan/site, lokasi, kategori titik, dan status layanan." },
};

const EMPTY = { nama_cabang: "", pemilik: "", gmail: "", password_gmail: "", password_starlink: "", sn_kit: "", status_alat: "", versi: "", paket: "", jatuh_tempo: "", nomor_layanan: "", lokasi: "", kategori: "", status_layanan: "", biaya_bulanan: "" };

const SEEDABLE = ["starlink", "indibiz"];

const salin = (text, label) => {
  try {
    navigator.clipboard?.writeText(text)?.then(() => toast.success(`${label} disalin`)).catch(() => toast.info(`${label}: ${text}`));
  } catch { toast.info(`${label}: ${text}`); }
};

const hariMenuju = (jt) => {
  if (!jt) return null;
  const d = new Date(jt);
  if (isNaN(d.getTime())) return null;
  const today = new Date(); today.setHours(0, 0, 0, 0);
  return Math.ceil((d - today) / 86400000);
};

const fmtJT = (jt) => {
  if (!jt) return "-";
  const d = new Date(jt);
  if (isNaN(d.getTime())) return jt;
  return fmtDate(jt);
};

const Badge = ({ children, className, big }) => (
  <span className={`px-1.5 py-0.5 rounded border font-mono ${big ? "text-lg px-3 py-1.5 font-bold" : "text-[9px]"} ${className}`}>{children}</span>
);

const DueBadge = ({ jt, testid }) => {
  const d = hariMenuju(jt);
  if (d === null) return jt ? <span className="font-mono text-[10px] text-muted-foreground" data-testid={testid}>{jt}</span> : null;
  if (d < 0) return <Badge className="bg-rose-500/10 text-rose-500 border-rose-500/20"><span data-testid={testid}>Lewat {-d} hari</span></Badge>;
  if (d <= 7) return <Badge className="bg-amber-500/10 text-amber-500 border-amber-500/20"><span data-testid={testid}>{d === 0 ? "Jatuh tempo hari ini" : `${d} hari lagi`}</span></Badge>;
  return <span className="font-mono text-[10px] text-muted-foreground" data-testid={testid}>JT: {jt}</span>;
};

const Cred = ({ label, value, testid }) => (
  <div className="flex items-center gap-1 font-mono text-[10px] min-w-0">
    <span className="text-muted-foreground shrink-0">{label}:</span>
    <span className="font-semibold truncate" data-testid={testid}>{value || "-"}</span>
    {value && (
      <button type="button" title={`Salin ${label}`} className="text-muted-foreground hover:text-foreground transition-colors shrink-0" onClick={() => salin(value, label)} data-testid={`${testid}-copy`}>
        <Copy className="w-3 h-3" />
      </button>
    )}
  </div>
);

export default function NetData({ kind }) {
  const meta = KIND_META[kind] || KIND_META.starlink;
  const Icon = meta.icon;
  const isIndibiz = meta.mode === "indibiz";
  const { user: me } = useAuth();
  const isAdmin = ["admin", "admin_fasnet"].includes(me?.role);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [filterPemilik, setFilterPemilik] = useState("all");
  const [filterEmail, setFilterEmail] = useState("");
  const [filterJT, setFilterJT] = useState("all");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [pin, setPin] = useState("");
  const [busy, setBusy] = useState(false);
  const [delItem, setDelItem] = useState(null);
  const [seedOpen, setSeedOpen] = useState(false);
  const [paidBusy, setPaidBusy] = useState("");
  const [openGroups, setOpenGroups] = useState(new Set());
  const [checkedIds, setCheckedIds] = useState({});
  const [bulkDel, setBulkDel] = useState(false);

  const load = async () => {
    try { const { data } = await api.get(`/netdata/${kind}`); setItems(data); } catch { /* non-admin */ }
    finally { setLoading(false); }
  };
  useEffect(() => { setItems([]); setLoading(true); load(); }, [kind]);

  if (!isAdmin) {
    return (
      <div className="flex flex-col items-center justify-center py-24 gap-3" data-testid={`${kind}-forbidden`}>
        <ShieldCheck className="w-10 h-10 text-rose-500" />
        <p className="text-muted-foreground text-sm">Hanya Admin Fasnet / Super Admin yang dapat membuka {meta.title}.</p>
      </div>
    );
  }

  const q = query.trim().toLowerCase();
  const searchFields = isIndibiz ? ["nama_cabang", "nomor_layanan", "lokasi", "kategori"] : ["nama_cabang", "pemilik", "gmail", "sn_kit"];
  const pemilikOptions = [...new Set(items.map((s) => (s.pemilik || "").trim()).filter(Boolean))].sort();
  const qEmail = filterEmail.trim().toLowerCase();
  const filtered = items.filter((s) => !q || searchFields.some((f) => (s[f] || "").toLowerCase().includes(q)))
    .filter((s) => filterPemilik === "all" || (s.pemilik || "").trim() === filterPemilik)
    .filter((s) => !qEmail || (s.gmail || "").toLowerCase().includes(qEmail))
    .filter((s) => {
      if (filterJT === "all") return true;
      const d = hariMenuju(s.jatuh_tempo);
      if (filterJT === "lewat") return d !== null && d < 0;
      if (filterJT === "7hari") return d !== null && d >= 0 && d <= 7;
      if (filterJT === "30hari") return d !== null && d > 7 && d <= 30;
      return d === null || d > 30;
    });
  // Data Starlink: urutkan per Gmail agar email yang sama bersebelahan, lalu bangun kelompok
  const grouped = kind === "starlink"
    ? [...filtered].sort((a, b) => (a.gmail || "~").toLowerCase().localeCompare((b.gmail || "~").toLowerCase()) || (a.nama_cabang || "").localeCompare(b.nama_cabang || ""))
    : filtered;
  const emailGroups = [];
  const groupSize = {};
  if (kind === "starlink") {
    const seenG = new Map();
    for (const s of grouped) {
      const g = (s.gmail || "-").toLowerCase();
      if (!seenG.has(g)) { const arr = []; seenG.set(g, arr); emailGroups.push(arr); }
      seenG.get(g).push(s);
    }
    emailGroups.forEach((grp) => { groupSize[(grp[0].gmail || "-").toLowerCase()] = grp.length; });
  }
  const lewat = items.filter((s) => { const d = hariMenuju(s.jatuh_tempo); return d !== null && d < 0; }).length;
  const dekat = items.filter((s) => { const d = hariMenuju(s.jatuh_tempo); return d !== null && d >= 0 && d <= 7; }).length;
  const aktif = items.filter((s) => (s.status_layanan || "").toLowerCase() === "aktif").length;
  const totalBiaya = items.filter((s) => (s.status_layanan || "").toLowerCase() === "aktif").reduce((a, s) => a + (Number(s.biaya_bulanan) || 0), 0);

  const openAdd = () => { setEditing(null); setForm(EMPTY); setPin(""); setOpen(true); };
  const openEdit = (s) => {
    setEditing(s);
    setForm(Object.fromEntries(Object.keys(EMPTY).map((k) => [k, s[k] || ""])));
    setPin(""); setOpen(true);
  };

  const markPaid = async (s) => {
    setPaidBusy(s.id);
    try {
      const { data } = await api.post(`/netdata/${kind}/${s.id}/sudah-bayar`);
      toast.success(`Sudah bayar — jatuh tempo ${s.nama_cabang} maju ke ${fmtJT(data.jatuh_tempo)}`);
      load();
    } catch (err) { toast.error(errMsg(err)); } finally { setPaidBusy(""); }
  };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/auth/verify-pin", { pin });
      const payload = { ...form, biaya_bulanan: Number(form.biaya_bulanan) || 0 };
      if (editing) {
        await api.put(`/netdata/${kind}/${editing.id}`, payload);
        toast.success(`${meta.title} diperbarui`);
      } else {
        await api.post(`/netdata/${kind}`, payload);
        toast.success(`${meta.title} ditambahkan`);
      }
      setOpen(false); load();
    } catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };

  const toggleGroup = (g) => setOpenGroups((prev) => { const n = new Set(prev); if (n.has(g)) n.delete(g); else n.add(g); return n; });
  // tiap email berbeda -> warna berbeda (email sama = warna sama karena data diurutkan per email)
  const EMAIL_COLORS = ["text-sky-500", "text-emerald-500", "text-violet-500", "text-amber-500", "text-pink-500", "text-cyan-600", "text-orange-500", "text-lime-600"];
  const EMAIL_ACCENTS = ["border-l-4 border-l-sky-500", "border-l-4 border-l-emerald-500", "border-l-4 border-l-violet-500", "border-l-4 border-l-amber-500", "border-l-4 border-l-pink-500", "border-l-4 border-l-cyan-600", "border-l-4 border-l-orange-500", "border-l-4 border-l-lime-600"];
  const emailColorIdx = (g) => {
    let h = 0;
    for (let i = 0; i < g.length; i++) h = (h * 31 + g.charCodeAt(i)) >>> 0;
    return h % EMAIL_COLORS.length;
  };
  const selectedIds = Object.keys(checkedIds).filter((k) => checkedIds[k]);
  const toggleAll = () => {
    if (filtered.length && selectedIds.length === filtered.length) setCheckedIds({});
    else setCheckedIds(Object.fromEntries(filtered.map((s) => [s.id, true])));
  };

  return (
    <div className="space-y-5" data-testid={`${kind}-page`}>
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight flex items-center gap-2">
            <Icon className="w-6 h-6 text-[#F04E23]" /> {meta.title}
          </h1>
          <p className="text-sm text-muted-foreground mt-1">{meta.subtitle}</p>
          <div className="flex gap-2 mt-2 flex-wrap">
            <Badge big className="bg-sky-500/10 text-sky-500 border-sky-500/20"><span data-testid={`${kind}-stat-total`}>Total {items.length} {meta.unit}</span></Badge>
            {isIndibiz && aktif > 0 && <Badge big className="bg-emerald-500/10 text-emerald-500 border-emerald-500/20"><span data-testid={`${kind}-stat-aktif`}>{aktif} aktif</span></Badge>}
            {isIndibiz && totalBiaya > 0 && <Badge big className="bg-[#F04E23]/10 text-[#F04E23] border-[#F04E23]/20"><span data-testid={`${kind}-stat-biaya`}>Total Rp {totalBiaya.toLocaleString("id-ID")}/bln</span></Badge>}
            {!isIndibiz && dekat > 0 && <Badge big className="bg-amber-500/10 text-amber-500 border-amber-500/20"><span data-testid={`${kind}-stat-dekat`}>{dekat} jatuh tempo ≤ 7 hari</span></Badge>}
            {!isIndibiz && lewat > 0 && <Badge big className="bg-rose-500/10 text-rose-500 border-rose-500/20"><span data-testid={`${kind}-stat-lewat`}>{lewat} lewat jatuh tempo</span></Badge>}
          </div>
        </div>
        <Button onClick={openAdd} data-testid={`btn-add-${kind}`}><Plus className="w-4 h-4 mr-1.5" /> Tambah {meta.title.replace("Data ", "")}</Button>
      </div>

      <Card className="bg-card border-border shadow-sm" data-testid={`${kind}-card`}>
        <CardHeader className="flex flex-row items-center justify-between gap-3 flex-wrap space-y-0">
          <CardTitle className="text-base font-heading">{isIndibiz ? `Daftar Akun (${filtered.length})` : `Daftar Unit (${filtered.length})`}</CardTitle>
          <div className="flex items-center gap-2 flex-wrap">
            {!isIndibiz && (
              <>
                <Select value={filterPemilik} onValueChange={setFilterPemilik}>
                  <SelectTrigger className="w-40 h-9" data-testid="select-filter-pemilik"><SelectValue placeholder="Pemilik" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all" data-testid="filter-pemilik-all">Semua Pemilik</SelectItem>
                    {pemilikOptions.map((p) => <SelectItem key={p} value={p} data-testid={`filter-pemilik-${p.replace(/\s+/g, "-")}`}>{p}</SelectItem>)}
                  </SelectContent>
                </Select>
                <Input value={filterEmail} onChange={(e) => setFilterEmail(e.target.value)} placeholder="Filter email..." className="w-48 h-9" data-testid="input-filter-email" />
                <Select value={filterJT} onValueChange={setFilterJT}>
                  <SelectTrigger className="w-44 h-9" data-testid="select-filter-jt"><SelectValue placeholder="Jatuh tempo" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all" data-testid="filter-jt-all">Semua Jatuh Tempo</SelectItem>
                    <SelectItem value="lewat" data-testid="filter-jt-lewat">Lewat jatuh tempo</SelectItem>
                    <SelectItem value="7hari" data-testid="filter-jt-7hari">7 hari ke depan</SelectItem>
                    <SelectItem value="30hari" data-testid="filter-jt-30hari">8–30 hari ke depan</SelectItem>
                    <SelectItem value="aman" data-testid="filter-jt-aman">Aman (&gt; 30 hari)</SelectItem>
                  </SelectContent>
                </Select>
                {kind === "starlink" && selectedIds.length > 0 && (
                  <Button variant="outline" size="sm" className="h-9 text-rose-500 border-rose-500/40 hover:bg-rose-500/10" onClick={() => setBulkDel(true)} data-testid="btn-bulk-del-starlink">
                    <Trash2 className="w-4 h-4 mr-1" /> Hapus Terpilih ({selectedIds.length})
                  </Button>
                )}
              </>
            )}
            <div className="relative">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <Input value={query} onChange={(e) => setQuery(e.target.value)} placeholder={isIndibiz ? "Cari pelanggan / no. layanan / lokasi..." : "Cari cabang / pemilik / gmail / SN..."} className="pl-9 w-64 h-9" data-testid={`input-${kind}-search`} />
            </div>
          </div>
        </CardHeader>
        {!loading && !filtered.length && (
          <div className="text-center py-8 text-muted-foreground text-sm space-y-3 px-4 pb-4" data-testid={`${kind}-empty`}>
            <p>{q ? "Data tidak ditemukan" : `Belum ada data — klik "Tambah ${meta.title.replace("Data ", "")}" untuk input manual`}</p>
            {!q && SEEDABLE.includes(kind) && me?.role === "admin" && (
              <Button variant="outline" size="sm" onClick={() => setSeedOpen(true)} data-testid={`btn-seed-${kind}`}>Impor Data Awal {meta.title.replace("Data ", "")}</Button>
            )}
          </div>
        )}
        {kind === "starlink" && filtered.length > 0 ? (
          <div className="overflow-x-auto px-2 pb-3" data-testid={`${kind}-grid`}>
            <Table className="min-w-[1320px]" data-testid="starlink-table">
              <TableHeader>
                <TableRow>
                  <TableHead className="w-8">
                    <input type="checkbox" aria-label="Pilih semua" className="accent-rose-500 w-3.5 h-3.5 cursor-pointer align-middle" checked={filtered.length > 0 && selectedIds.length === filtered.length} onChange={toggleAll} data-testid="chk-starlink-all" />
                  </TableHead>
                  <TableHead className="w-10">No.</TableHead>
                  <TableHead>Nama Cabang</TableHead>
                  <TableHead>Pemilik Cabang</TableHead>
                  <TableHead>Gmail Login Starlink</TableHead>
                  <TableHead>Password Gmail</TableHead>
                  <TableHead>Password Login Starlink</TableHead>
                  <TableHead>SN KIT</TableHead>
                  <TableHead>Status Alat</TableHead>
                  <TableHead>Versi Starlink</TableHead>
                  <TableHead>Paket</TableHead>
                  <TableHead>Biaya/bln</TableHead>
                  <TableHead>Jatuh Tempo</TableHead>
                  <TableHead className="text-right">Aksi</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {(() => {
                  let rowNum = 0;
                  const renderRow = (s, opts = {}) => {
                    const { colorCls = "", accentCls = "", toggle = null } = opts;
                    rowNum += 1;
                    const sisa = hariMenuju(s.jatuh_tempo);
                    const overdue = sisa !== null && sisa < 0;
                    return (
                      <TableRow key={s.id} className={`${overdue ? "bg-rose-500/5" : ""} ${accentCls}`.trim()} data-testid={`${kind}-item-${s.id}`}>
                        <TableCell>
                          <input type="checkbox" aria-label={`Pilih ${s.nama_cabang}`} className="accent-rose-500 w-3.5 h-3.5 cursor-pointer align-middle" checked={!!checkedIds[s.id]} onChange={() => setCheckedIds((p) => ({ ...p, [s.id]: !p[s.id] }))} data-testid={`chk-starlink-${s.id}`} />
                        </TableCell>
                        <TableCell className="font-mono text-xs text-muted-foreground">{rowNum}</TableCell>
                        <TableCell className="font-semibold text-xs whitespace-nowrap">{s.nama_cabang}</TableCell>
                        <TableCell className="text-xs whitespace-nowrap">{s.pemilik || "-"}</TableCell>
                        <TableCell className="font-mono text-xs">
                          <span className="inline-flex items-center gap-1">
                            {toggle && (
                              <button type="button" title={toggle.open ? "Ciutkan grup email ini" : `Lihat ${toggle.size} unit dengan email ini`} className="text-muted-foreground hover:text-foreground transition-colors" onClick={toggle.onToggle} data-testid={`${kind}-gmail-${s.id}-toggle`}>
                                {toggle.open ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}
                              </button>
                            )}
                            <span className={colorCls || undefined}>{s.gmail || "-"}</span>
                            {toggle && <span className="text-[9px] text-muted-foreground font-semibold" data-testid={`${kind}-gmail-${s.id}-count`}>({toggle.size} unit)</span>}
                            {s.gmail && <button type="button" title="Salin Email" className="text-muted-foreground hover:text-foreground transition-colors" onClick={() => salin(s.gmail, "Email")} data-testid={`${kind}-gmail-${s.id}-copy`}><Copy className="w-3 h-3" /></button>}
                          </span>
                        </TableCell>
                        <TableCell className="font-mono text-xs">
                          <span className="inline-flex items-center gap-1">{s.password_gmail || "-"}
                            {s.password_gmail && <button type="button" title="Salin Password Gmail" className="text-muted-foreground hover:text-foreground transition-colors" onClick={() => salin(s.password_gmail, "Password Gmail")} data-testid={`${kind}-passgmail-${s.id}-copy`}><Copy className="w-3 h-3" /></button>}
                          </span>
                        </TableCell>
                        <TableCell className="font-mono text-xs">
                          <span className="inline-flex items-center gap-1">{s.password_starlink || "-"}
                            {s.password_starlink && <button type="button" title="Salin Password Starlink" className="text-muted-foreground hover:text-foreground transition-colors" onClick={() => salin(s.password_starlink, "Password Starlink")} data-testid={`${kind}-passl-${s.id}-copy`}><Copy className="w-3 h-3" /></button>}
                          </span>
                        </TableCell>
                        <TableCell className="font-mono text-xs" data-testid={`${kind}-sn-${s.id}`}>{s.sn_kit || "-"}</TableCell>
                        <TableCell><Badge className={(s.status_alat || "").toUpperCase() === "BAYAR" ? "bg-emerald-500/10 text-emerald-500 border-emerald-500/20" : "bg-slate-500/10 text-slate-400 border-slate-500/20"}>{s.status_alat || "-"}</Badge></TableCell>
                        <TableCell className="font-mono text-xs">{s.versi || "-"}</TableCell>
                        <TableCell className="font-mono text-xs">{s.paket || "-"}</TableCell>
                        <TableCell className="font-mono text-xs text-[#F04E23]" data-testid={`${kind}-biaya-${s.id}`}>{Number(s.biaya_bulanan) > 0 ? `Rp ${Number(s.biaya_bulanan).toLocaleString("id-ID")}` : "-"}</TableCell>
                        <TableCell className={`font-mono text-xs whitespace-nowrap ${overdue ? "text-rose-400 font-bold" : ""}`} data-testid={`${kind}-due-${s.id}`}>{fmtJT(s.jatuh_tempo)}</TableCell>
                        <TableCell className="text-right">
                          <div className="flex items-center gap-1 justify-end">
                            {s.jatuh_tempo && (
                              <Button size="sm" variant="outline" className="h-6 px-1.5 text-[10px] text-emerald-500 border-emerald-500/40 hover:bg-emerald-500/10" title="Tandai sudah bayar — jatuh tempo otomatis maju ke tanggal yang sama bulan berikutnya" onClick={() => markPaid(s)} disabled={paidBusy === s.id} data-testid={`btn-paid-${kind}-${s.id}`}>
                                <BadgeCheck className="w-3 h-3 mr-0.5" /> {paidBusy === s.id ? "..." : "Sudah Bayar"}
                              </Button>
                            )}
                            <Button size="sm" variant="outline" className="h-6 w-6 p-0" title="Edit data" onClick={() => openEdit(s)} data-testid={`btn-edit-${kind}-${s.id}`}>
                              <Pencil className="w-3 h-3" />
                            </Button>
                            <Button size="sm" variant="outline" className="h-6 w-6 p-0 text-rose-500 border-rose-500/40 hover:bg-rose-500/10" title="Hapus data" onClick={() => setDelItem(s)} data-testid={`btn-del-${kind}-${s.id}`}>
                              <Trash2 className="w-3 h-3" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  };
                  return grouped.map((s, idx) => {
                    const g = (s.gmail || "-").toLowerCase();
                    const size = groupSize[g] || 1;
                    const cidx = s.gmail ? emailColorIdx(g) : -1;
                    const colorCls = cidx >= 0 ? EMAIL_COLORS[cidx] : "";
                    const accentCls = size > 1 && cidx >= 0 ? EMAIL_ACCENTS[cidx] : "";
                    const isFirst = idx === 0 || ((grouped[idx - 1].gmail || "-").toLowerCase()) !== g;
                    if (size > 1) {
                      const isOpen = openGroups.has(g);
                      if (!isFirst) return isOpen ? renderRow(s, { colorCls, accentCls }) : null;
                      return renderRow(s, { colorCls, accentCls, toggle: { open: isOpen, size, onToggle: () => toggleGroup(g) } });
                    }
                    return renderRow(s, { colorCls });
                  });
                })()}
              </TableBody>
            </Table>
          </div>
        ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2 px-4 pb-4" data-testid={`${kind}-grid`}>
          {filtered.map((s, idx) => (
            <div key={s.id} className="rounded-xl border border-border bg-accent/20 px-3 py-2.5 space-y-1" data-testid={`${kind}-item-${s.id}`}>
              <div className="flex items-center gap-2">
                <span className="font-mono text-[10px] text-muted-foreground shrink-0 w-6 text-right">{idx + 1}</span>
                <span className="font-semibold text-xs truncate">{s.nama_cabang}</span>
                <div className="flex items-center gap-1 ml-auto shrink-0">
                  <Button size="sm" variant="outline" className="h-6 w-6 p-0" title="Edit data" onClick={() => openEdit(s)} data-testid={`btn-edit-${kind}-${s.id}`}>
                    <Pencil className="w-3 h-3" />
                  </Button>
                  <Button size="sm" variant="outline" className="h-6 w-6 p-0 text-rose-500 border-rose-500/40 hover:bg-rose-500/10" title="Hapus data" onClick={() => setDelItem(s)} data-testid={`btn-del-${kind}-${s.id}`}>
                    <Trash2 className="w-3 h-3" />
                  </Button>
                </div>
              </div>
              {isIndibiz ? (
                <>
                  <div className="pl-8 flex items-center gap-1.5 flex-wrap">
                    {s.kategori && <Badge className="bg-sky-500/10 text-sky-500 border-sky-500/20">{s.kategori}</Badge>}
                    {s.status_layanan && (
                      <Badge className={(s.status_layanan || "").toLowerCase() === "aktif" ? "bg-emerald-500/10 text-emerald-500 border-emerald-500/20" : "bg-rose-500/10 text-rose-500 border-rose-500/20"}>
                        <span data-testid={`${kind}-status-${s.id}`}>{s.status_layanan}</span>
                      </Badge>
                    )}
                    {s.lokasi && <span className="text-[10px] text-muted-foreground">Lokasi: {s.lokasi}</span>}
                  </div>
                  <div className="pl-8 space-y-0.5">
                    <Cred label="No. Layanan" value={s.nomor_layanan} testid={`${kind}-nolayanan-${s.id}`} />
                    <div className="flex items-center gap-1 font-mono text-[10px]">
                      <span className="text-muted-foreground shrink-0">Biaya:</span>
                      <span className="font-semibold text-[#F04E23]" data-testid={`${kind}-biaya-${s.id}`}>{Number(s.biaya_bulanan) > 0 ? `Rp ${Number(s.biaya_bulanan).toLocaleString("id-ID")}/bln` : "-"}</span>
                    </div>
                  </div>
                </>
              ) : (
                <>
                  <div className="pl-8 flex items-center gap-1.5 flex-wrap">
                    {s.versi && <Badge className="bg-sky-500/10 text-sky-500 border-sky-500/20">{s.versi}</Badge>}
                    {s.paket && <Badge className="bg-emerald-500/10 text-emerald-500 border-emerald-500/20">{s.paket}</Badge>}
                    {s.status_alat && <Badge className="bg-slate-500/10 text-slate-400 border-slate-500/20">{s.status_alat}</Badge>}
                    {s.pemilik && <span className="text-[10px] text-muted-foreground">Pemilik: {s.pemilik}</span>}
                  </div>
                  <div className="pl-8 space-y-0.5">
                    <Cred label="Gmail" value={s.gmail} testid={`${kind}-gmail-${s.id}`} />
                    <Cred label="Pass Gmail" value={s.password_gmail} testid={`${kind}-passgmail-${s.id}`} />
                    <Cred label={`Pass ${meta.title.replace("Data ", "")}`} value={s.password_starlink} testid={`${kind}-passl-${s.id}`} />
                  </div>
                  <div className="pl-8 flex items-center justify-between gap-2">
                    <span className="font-mono text-[10px] text-muted-foreground truncate" data-testid={`${kind}-sn-${s.id}`}>{s.sn_kit || "-"}</span>
                    <DueBadge jt={s.jatuh_tempo} testid={`${kind}-due-${s.id}`} />
                  </div>
                  {Number(s.biaya_bulanan) > 0 && (
                    <div className="pl-8 flex items-center gap-1 font-mono text-[10px]">
                      <span className="text-muted-foreground shrink-0">Biaya:</span>
                      <span className="font-semibold text-[#F04E23]" data-testid={`${kind}-biaya-${s.id}`}>Rp {Number(s.biaya_bulanan).toLocaleString("id-ID")}/bln</span>
                    </div>
                  )}
                </>
              )}
            </div>
          ))}
        </div>
        )}
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid={`dialog-${kind}`}>
          <DialogHeader><DialogTitle className="font-heading">{editing ? `Edit — ${editing.nama_cabang}` : `Tambah ${meta.title.replace("Data ", "")}`}</DialogTitle></DialogHeader>
          <form onSubmit={submit} className="space-y-3">
            {isIndibiz ? (
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5 col-span-2"><Label>Nama Pelanggan / Site</Label><Input value={form.nama_cabang} onChange={(e) => setForm({ ...form, nama_cabang: e.target.value })} required placeholder="Contoh: Warkop Eva Embaloh" data-testid={`input-${kind}-nama`} /></div>
                <div className="space-y-1.5"><Label>Nomor Layanan / Internet (ID)</Label><Input inputMode="numeric" value={form.nomor_layanan} onChange={(e) => setForm({ ...form, nomor_layanan: e.target.value })} placeholder="162601832579" data-testid={`input-${kind}-nolayanan`} /></div>
                <div className="space-y-1.5"><Label>Estimasi Lokasi / Wilayah</Label><Input value={form.lokasi} onChange={(e) => setForm({ ...form, lokasi: e.target.value })} placeholder="Embaloh" data-testid={`input-${kind}-lokasi`} /></div>
                <div className="space-y-1.5"><Label>Kategori / Tipe Titik</Label><Input value={form.kategori} onChange={(e) => setForm({ ...form, kategori: e.target.value })} placeholder="Mitra Warkop / Reseller" data-testid={`input-${kind}-kategori`} /></div>
                <div className="space-y-1.5"><Label>Status Layanan</Label><Input value={form.status_layanan} onChange={(e) => setForm({ ...form, status_layanan: e.target.value })} placeholder="Aktif" data-testid={`input-${kind}-statuslayanan`} /></div>
                <div className="space-y-1.5 col-span-2"><Label>Biaya Bulanan (Rp)</Label><Input type="number" min="0" inputMode="numeric" value={form.biaya_bulanan} onChange={(e) => setForm({ ...form, biaya_bulanan: e.target.value })} placeholder="Contoh: 300000" data-testid={`input-${kind}-biaya`} /></div>
              </div>
            ) : (
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5 col-span-2"><Label>Nama Cabang</Label><Input value={form.nama_cabang} onChange={(e) => setForm({ ...form, nama_cabang: e.target.value })} required data-testid={`input-${kind}-nama`} /></div>
                <div className="space-y-1.5"><Label>Pemilik Cabang</Label><Input value={form.pemilik} onChange={(e) => setForm({ ...form, pemilik: e.target.value })} data-testid={`input-${kind}-pemilik`} /></div>
                <div className="space-y-1.5"><Label>SN Perangkat</Label><Input value={form.sn_kit} onChange={(e) => setForm({ ...form, sn_kit: e.target.value })} data-testid={`input-${kind}-sn`} /></div>
                <div className="space-y-1.5 col-span-2"><Label>Gmail Login</Label><Input value={form.gmail} onChange={(e) => setForm({ ...form, gmail: e.target.value })} placeholder="nama@gmail.com" data-testid={`input-${kind}-gmail`} /></div>
                <div className="space-y-1.5"><Label>Password Gmail</Label><Input value={form.password_gmail} onChange={(e) => setForm({ ...form, password_gmail: e.target.value })} data-testid={`input-${kind}-passgmail`} /></div>
                <div className="space-y-1.5"><Label>Password Login {meta.title.replace("Data ", "")}</Label><Input value={form.password_starlink} onChange={(e) => setForm({ ...form, password_starlink: e.target.value })} data-testid={`input-${kind}-passl`} /></div>
                <div className="space-y-1.5"><Label>Status Alat</Label><Input value={form.status_alat} onChange={(e) => setForm({ ...form, status_alat: e.target.value })} placeholder="TITIP / BAYAR" data-testid={`input-${kind}-status`} /></div>
                <div className="space-y-1.5"><Label>Versi Perangkat</Label><Input value={form.versi} onChange={(e) => setForm({ ...form, versi: e.target.value })} placeholder="GEN 3" data-testid={`input-${kind}-versi`} /></div>
                <div className="space-y-1.5"><Label>Paket</Label><Input value={form.paket} onChange={(e) => setForm({ ...form, paket: e.target.value })} placeholder="RESIDEN / JELAJAH" data-testid={`input-${kind}-paket`} /></div>
                <div className="space-y-1.5"><Label>Jatuh Tempo</Label><DatePicker value={form.jatuh_tempo} onChange={(v) => setForm({ ...form, jatuh_tempo: v })} placeholder="Pilih tanggal jatuh tempo" testid={`input-${kind}-jatuh-tempo`} /></div>
                <div className="space-y-1.5 col-span-2"><Label>Biaya Langganan / Bulan (Rp)</Label><Input type="number" min="0" inputMode="numeric" value={form.biaya_bulanan} onChange={(e) => setForm({ ...form, biaya_bulanan: e.target.value })} placeholder="Contoh: 750000 — dipakai untuk hitung Pendapatan Kotor" data-testid={`input-${kind}-biaya`} /></div>
              </div>
            )}
            <div className="space-y-1.5"><Label>PIN Super Admin (wajib)</Label><Input type="password" inputMode="numeric" value={pin} onChange={(e) => setPin(e.target.value)} required placeholder="Masukkan PIN" data-testid={`input-${kind}-admin-pin`} /></div>
            <Button type="submit" className="w-full" disabled={busy} data-testid={`btn-save-${kind}`}>{busy ? "Memproses..." : editing ? "Simpan Perubahan" : "Simpan"}</Button>
          </form>
        </DialogContent>
      </Dialog>

      <PinConfirm
        open={!!delItem}
        onOpenChange={() => setDelItem(null)}
        title={`Hapus ${meta.title}`}
        description={`Data ${delItem?.nama_cabang || ""} akan dihapus permanen. Masukkan PIN untuk konfirmasi.`}
        confirmLabel="Hapus Data"
        destructive
        testid={`pin-del-${kind}`}
        onConfirm={async () => {
          await api.delete(`/netdata/${kind}/${delItem.id}`);
          toast.success(`${meta.title} dihapus`);
          setDelItem(null);
          load();
        }}
      />

      <PinConfirm
        open={bulkDel}
        onOpenChange={() => setBulkDel(false)}
        title="Hapus Masal Data Starlink"
        description={`${selectedIds.length} data terpilih akan dihapus permanen. Masukkan PIN untuk konfirmasi.`}
        confirmLabel="Hapus Semua"
        destructive
        testid="pin-bulk-del-starlink"
        onConfirm={async () => {
          const { data } = await api.post(`/netdata/${kind}/bulk-delete`, { ids: selectedIds });
          toast.success(`${data.deleted} data dihapus`);
          setCheckedIds({});
          setBulkDel(false);
          load();
        }}
      />

      <PinConfirm
        open={seedOpen}
        onOpenChange={() => setSeedOpen(false)}
        title={`Impor Data Awal ${meta.title.replace("Data ", "")}`}
        description="Data bawaan akan diimpor ke database. Data yang sudah ada tidak akan ditimpa atau digandakan."
        confirmLabel="Impor Data"
        testid={`pin-seed-${kind}`}
        onConfirm={async () => {
          const { data } = await api.post(`/netdata/${kind}/seed-defaults`);
          toast.success(`Impor selesai: ${data.inserted} data baru, ${data.skipped} dilewati`);
          setSeedOpen(false);
          load();
        }}
      />
    </div>
  );
}
